import { batch, signal } from '@preact/signals-react';
import { Preset } from '../../../common/models/valueObjects/Preset';
import { TrendData } from '../../../common/dashboards/models/TrendData';
import { Moment } from 'moment';
import { DateRange } from '@mui/x-date-pickers-pro';

export type State = {
  name: string;
  profiles: Preset[];
  projects: Preset[];
  tags: string[];
  overviews: string[];
  trends: TrendData[];
  withExceptions: boolean;
  includePassed?: boolean;
  dateRange: DateRange<Moment>;
  ready: boolean;
};

export enum ExportFormat {
  PRINT = 'print',
  CSV = 'csv',
}

const DEFAULT_STATE: State = {
  name: '',
  profiles: [],
  projects: [],
  tags: [],
  overviews: [],
  trends: [],
  withExceptions: false,
  includePassed: true,
  dateRange: [null, null],
  ready: false,
};

export class DashboardExportState {
  private static instance: DashboardExportState | null = null;

  private readonly name = signal('');
  private readonly selectedOverviews = signal(<string[]>[]);
  private readonly trends = signal(<TrendData[]>[]);
  private readonly withExceptions = signal(false);
  private readonly includePassed = signal(true);
  private readonly dateRange = signal(<DateRange<Moment>>[null, null]);

  readonly ready = signal(false);
  readonly profiles = signal(<Preset[]>[]);
  readonly projects = signal(<Preset[]>[]);
  readonly tags = signal(<string[]>[]);

  readonly format = signal(ExportFormat.PRINT);

  static getInstance() {
    if (DashboardExportState.instance === null) {
      DashboardExportState.instance = new DashboardExportState();
    }
    return DashboardExportState.instance;
  }

  setState(state: State) {
    batch(() => {
      this.name.value = state.name;
      this.profiles.value = state.profiles;
      this.projects.value = state.projects;
      this.tags.value = state.tags;
      this.selectedOverviews.value = state.overviews;
      this.trends.value = state.trends;
      this.withExceptions.value = state.withExceptions;
      this.dateRange.value = state.dateRange;
      if (state.includePassed !== undefined) {
        this.includePassed.value = state.includePassed;
      }
      this.ready.value = state.ready;
    });
  }

  reset() {
    this.setState({ ...DEFAULT_STATE });
  }

  peek() {
    return {
      name: this.name.peek(),
      projects: this.projects.peek(),
      profiles: this.profiles.peek(),
      tags: this.tags.peek(),
      selectedOverviews: this.selectedOverviews.peek(),
      trends: this.trends.peek(),
      withExceptions: this.withExceptions.peek(),
      includePassed: this.includePassed.peek(),
      dateRange: this.dateRange.peek(),
      format: this.format.peek(),
    };
  }

  selectOverviews(overviews: string[]) {
    this.selectedOverviews.value = overviews;
  }

  setIncludePassed(includePassed: boolean) {
    this.includePassed.value = includePassed;
  }

  setFormat(format: ExportFormat) {
    this.format.value = format;
  }
}
